import { FC, useEffect, useState } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { parseISO, subDays } from 'date-fns';

import 'react-day-picker/style.css';

import { formatDateToISO, formatInstantAsDay, parseFlexibleDate } from 'support/helpers/dateTime/dateTime';
import { PropertyFilter } from 'support/types/filters';
import { Input } from 'components/Form/Input/Input/Input';

import { CALENDAR_MODIFIERS_STYLES, CALENDAR_STYLES, COMPACT_CALENDAR_STYLE } from './FilterDatePanel.styles';

type FilterDatePanelProps = {
  filter: PropertyFilter;
  onChange: (filterKey: string, newValue: string) => void;
};

type Preset = {
  labelKey: string;
  getRange: (today: Date) => { from: Date; to: Date };
};

/**
 * Preset labelKeys are passed to t() via `t(preset.labelKey)`, which the
 * i18next-parser cannot follow. Enumerate them so they are not dropped.
 *
 * i18n keys
 * t('common:components.filters.date.presets.today')
 * t('common:components.filters.date.presets.yesterday')
 * t('common:components.filters.date.presets.last7Days')
 * t('common:components.filters.date.presets.last30Days')
 * t('common:components.filters.date.presets.last90Days')
 */
const PRESETS: Array<Preset> = [
  { labelKey: 'common:components.filters.date.presets.today', getRange: (today) => ({ from: today, to: today }) },
  {
    labelKey: 'common:components.filters.date.presets.yesterday',
    getRange: (today) => ({ from: subDays(today, 1), to: subDays(today, 1) }),
  },
  {
    labelKey: 'common:components.filters.date.presets.last7Days',
    // N - 1 so the inclusive range spans exactly N calendar days (today included).
    getRange: (today) => ({ from: subDays(today, 6), to: today }),
  },
  {
    labelKey: 'common:components.filters.date.presets.last30Days',
    getRange: (today) => ({ from: subDays(today, 29), to: today }),
  },
  {
    labelKey: 'common:components.filters.date.presets.last90Days',
    getRange: (today) => ({ from: subDays(today, 89), to: today }),
  },
];

export const FilterDatePanel: FC<FilterDatePanelProps> = ({ filter, onChange }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<DateRange | undefined>(() => toRange(filter.value));
  const [fromText, setFromText] = useState(() => formatInput(toRange(filter.value)?.from));
  const [toText, setToText] = useState(() => formatInput(toRange(filter.value)?.to));
  const [month, setMonth] = useState<Date>(() => toRange(filter.value)?.from ?? new Date());

  // Keep local selection/inputs in sync with external resets (badge removal, clear-all).
  useEffect(() => {
    const nextRange = toRange(filter.value);
    setRange(nextRange);
    setFromText(formatInput(nextRange?.from));
    setToText(formatInput(nextRange?.to));
    if (nextRange?.from) {
      setMonth(nextRange.from);
    }
  }, [filter.value]);

  // A range needs both ends; any incomplete range resets the filter so the parent value never goes stale.
  const emit = (nextRange?: DateRange) => {
    onChange(
      filter.key,
      nextRange?.from && nextRange?.to ? `${formatDateToISO(nextRange.from)},${formatDateToISO(nextRange.to)}` : '',
    );
  };

  // From the calendar / presets: drive both the range and the text inputs.
  const commitRange = (nextRange?: DateRange) => {
    setRange(nextRange);
    setFromText(formatInput(nextRange?.from));
    setToText(formatInput(nextRange?.to));
    const focusDate = nextRange?.from ?? nextRange?.to;
    if (focusDate) {
      setMonth(focusDate);
    }
    emit(nextRange);
  };

  // From typing: mask the input (auto-slashes) and only update the range once it parses (or is cleared).
  const handleTextChange = (edge: 'from' | 'to') => (raw: string) => {
    const setText = edge === 'from' ? setFromText : setToText;
    const masked = maskDate(raw, edge === 'from' ? fromText : toText);
    setText(masked);

    const parsed = masked ? tryParse(masked) : undefined;
    if (masked && !parsed) {
      return; // incomplete/invalid input — wait for a valid value
    }

    const nextRange: DateRange = {
      from: edge === 'from' ? parsed : range?.from,
      to: edge === 'to' ? parsed : range?.to,
    };
    setRange(nextRange);
    if (parsed) {
      setMonth(parsed); // follow the typed date in the calendar
    }
    emit(nextRange);
  };

  // Re-format to the canonical display once the user leaves the field.
  const handleBlur = (edge: 'from' | 'to') => () => {
    const setText = edge === 'from' ? setFromText : setToText;
    setText(formatInput(range?.[edge]));
  };

  return (
    <div className="flex w-72 flex-col gap-3 p-3" onKeyDown={(event) => event.stopPropagation()}>
      <Input
        label={t('common:components.filters.date.from')}
        value={fromText}
        placeholder="dd/mm/yyyy"
        leftIcon={<CalendarIcon className="size-5" />}
        onChange={(event) => handleTextChange('from')(event.target.value)}
        onBlur={handleBlur('from')}
        id={`filter-date-from-${filter.key}`}
      />
      <Input
        label={t('common:components.filters.date.to')}
        value={toText}
        placeholder="dd/mm/yyyy"
        leftIcon={<CalendarIcon className="size-5" />}
        onChange={(event) => handleTextChange('to')(event.target.value)}
        onBlur={handleBlur('to')}
        id={`filter-date-to-${filter.key}`}
      />

      <DayPicker
        mode="range"
        selected={range}
        onSelect={commitRange}
        month={month}
        onMonthChange={setMonth}
        disabled={filter.endDateLimit ? { before: filter.endDateLimit } : undefined}
        className="text-sm"
        style={COMPACT_CALENDAR_STYLE}
        styles={CALENDAR_STYLES}
        modifiersStyles={CALENDAR_MODIFIERS_STYLES}
      />

      <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
        {PRESETS.map((preset) => (
          <button
            key={preset.labelKey}
            type="button"
            onClick={() => commitRange(preset.getRange(new Date()))}
            className="rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-hidden"
          >
            {t(preset.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
};

const toRange = (value: string): DateRange | undefined => {
  if (!value) {
    return undefined;
  }
  const [from, to] = value.split(',');
  // A bare `yyyy-MM-dd` parsed with `new Date` is UTC midnight, the previous day west of UTC; `parseISO`
  // reads it as local midnight, so the day survives the round trip through `formatDateToISO`.
  return { from: from ? parseISO(from) : undefined, to: to ? parseISO(to) : undefined };
};

const formatInput = (date?: Date): string => (date ? formatInstantAsDay(date) : '');

// Auto-insert slashes as soon as a group is complete: "25" -> "25/", "2505" -> "25/05/",
// "25052026" -> "25/05/2026". `previous` lets the user delete an auto-inserted slash (backspace)
// instead of it immediately reappearing. Strips other non-digits and caps at 8 digits.
const maskDate = (raw: string, previous = ''): string => {
  if (previous.endsWith('/') && raw === previous.slice(0, -1)) {
    return raw; // user backspaced the auto-inserted slash — let it go
  }

  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  let result = day;
  if (day.length === 2) {
    result += `/${month}`;
  }
  if (month.length === 2) {
    result += `/${year}`;
  }
  return result;
};

const tryParse = (raw: string): Date | undefined => {
  try {
    return parseFlexibleDate(raw.trim());
  } catch {
    return undefined;
  }
};
